/**
 * ProCommerce service-page stat counters.
 * Animates the first numeric token inside a stat value while preserving
 * prefixes/suffixes such as "+ yrs", "+", "%", "$", and commas.
 */
(() => {
  const SELECTOR = '.pc-auto-counter[data-pc-counter-value]';

  if (window.ProCommerceAutoCounter) {
    window.ProCommerceAutoCounter.init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parseDisplayValue = (displayValue) => {
    const source = String(displayValue || '').trim();
    const match = source.match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) return null;

    const rawNumber = match[0];
    const numericValue = Number.parseFloat(rawNumber.replace(/,/g, ''));
    if (!Number.isFinite(numericValue)) return null;

    const decimalPart = rawNumber.split('.')[1];

    return {
      source,
      target: numericValue,
      prefix: source.slice(0, match.index),
      suffix: source.slice((match.index || 0) + rawNumber.length),
      decimals: decimalPart ? decimalPart.length : 0,
      useGrouping: rawNumber.includes(','),
    };
  };

  const formatNumber = (value, config) => {
    const rounded = config.decimals > 0
      ? Number(value.toFixed(config.decimals))
      : Math.round(value);

    if (config.useGrouping) {
      return rounded.toLocaleString('en-US', {
        minimumFractionDigits: config.decimals,
        maximumFractionDigits: config.decimals,
      });
    }

    return config.decimals > 0 ? rounded.toFixed(config.decimals) : String(rounded);
  };

  const setValue = (element, value, config) => {
    element.textContent = `${config.prefix}${formatNumber(value, config)}${config.suffix}`;
  };

  const animate = (element) => {
    if (!element || element.dataset.pcCounterAnimated === 'true') return;

    const config = parseDisplayValue(element.dataset.pcCounterValue || element.textContent);
    if (!config) {
      element.dataset.pcCounterAnimated = 'true';
      return;
    }

    const parsedStart = Number.parseFloat(element.dataset.pcCounterStart || '0');
    const start = Number.isFinite(parsedStart) ? parsedStart : 0;
    const parsedDuration = Number.parseInt(element.dataset.pcCounterDuration || '1400', 10);
    const duration = Number.isFinite(parsedDuration) ? Math.max(250, parsedDuration) : 1400;

    element.dataset.pcCounterAnimated = 'true';

    if (reducedMotion || start === config.target) {
      element.textContent = config.source;
      return;
    }

    setValue(element, start, config);
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      // Ease-out cubic keeps the count energetic at the start and smooth at the finish.
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (config.target - start) * eased;

      if (progress < 1) {
        setValue(element, current, config);
        requestAnimationFrame(tick);
      } else {
        // Restore the merchant-entered value exactly at the end.
        element.textContent = config.source;
      }
    };

    requestAnimationFrame(tick);
  };

  const observe = (element) => {
    if (!element || element.dataset.pcCounterObserved === 'true') return;
    element.dataset.pcCounterObserved = 'true';

    // Text-only values such as "Fiverr Pro" and "Top Rated" should never flash/change.
    if (!parseDisplayValue(element.dataset.pcCounterValue || element.textContent)) {
      element.dataset.pcCounterAnimated = 'true';
      return;
    }

    if (reducedMotion || !('IntersectionObserver' in window)) {
      animate(element);
      return;
    }

    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animate(entry.target);
        instance.unobserve(entry.target);
      });
    }, {
      threshold: 0.35,
      rootMargin: '0px 0px -5% 0px',
    });

    observer.observe(element);
  };

  const init = (root = document) => {
    if (!root) return;

    if (root.matches && root.matches(SELECTOR)) observe(root);
    root.querySelectorAll?.(SELECTOR).forEach(observe);
  };

  window.ProCommerceAutoCounter = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  // Shopify theme editor dynamically reloads sections without a full page refresh.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
